/**
 * web-session-store.ts — browser (`localStorage`) wiring for the session-result
 * store (Phase 7 scoring, task #4).
 *
 * `@muzly/shared` is also resolved by the React Native app, whose tsconfig has
 * no `lib.dom`, so `localStorage` is funnelled through `globalThis` (mirroring
 * `utils/native-host.ts`) rather than referenced bare. When no DOM storage is
 * present (SSR/native), the adapter degrades to an in-memory store so callers
 * never crash — RN should inject an AsyncStorage adapter instead.
 */

import {
  DEFAULT_SESSION_STORE_OPTIONS,
  createMemoryKeyValueStore,
  createSessionResultStore,
} from './session-result-store';

import type {
  KeyValueStore,
  SessionResultStore,
  SessionResultStoreOptions,
} from './session-result-store';

interface WebStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const getLocalStorage = (): WebStorageLike | undefined => {
  const g = globalThis as { localStorage?: WebStorageLike };
  const ls = g.localStorage;
  return ls && typeof ls.getItem === 'function' ? ls : undefined;
};

/** Promise-wrapped `localStorage`, or in-memory when DOM storage is absent. */
export const createLocalStorageKeyValueStore = (): KeyValueStore => {
  const ls = getLocalStorage();
  if (!ls) return createMemoryKeyValueStore();
  return {
    getItem: (key) => Promise.resolve(ls.getItem(key)),
    setItem: (key, value) => {
      ls.setItem(key, value);
      return Promise.resolve();
    },
    removeItem: (key) => {
      ls.removeItem(key);
      return Promise.resolve();
    },
  };
};

/** Ready-to-use web store backed by `localStorage`. */
export const createWebSessionResultStore = (
  opts: SessionResultStoreOptions = DEFAULT_SESSION_STORE_OPTIONS,
): SessionResultStore =>
  createSessionResultStore(createLocalStorageKeyValueStore(), opts);
